import { Loader2, ThumbsUp, User } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ImageOrVideo from "../tool_components/ImageOrVideo";
import { Button } from "@core/button";
import { Text } from "@core/text";
import { CourseData } from "@packages/constant/CourseData";
interface CardItem {
  id: string;
  imageUrl: string;
  videoUrl?: string;
  prompt: string;
  authorName: string;
  likeCount: number;
  courseName: string;
  gradeLevel: number;
  didUserLiked: boolean;
  createdAt: string;
}
interface FeaturedCardProps {
  item: CardItem;
  selectItem: (item: CardItem) => void;
  onLike: () => void;
  className?: string;
}

const FeaturedCard = ({
  item,
  selectItem,
  onLike,
  className,
}: FeaturedCardProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  return (
    <div
      key={item.id}
      onClick={() => selectItem(item)}
      className={`flex-shrink-0 w-80 bg-white/90 dark:bg-gray-800/90 rounded-xl shadow-lg border border-white/50 dark:border-gray-700/50 overflow-hidden transition-all duration-300 cursor-pointer hover:shadow-xl transform hover:-translate-y-1 ${className}`}
    >
      <div className="relative h-40 overflow-hidden">
        {item.imageUrl ? (
          <ImageOrVideo
            imageUrl={item.imageUrl}
            videoUrl={item.videoUrl}
            isLoading={false}
            resolution="high"
            createdAt={item.createdAt}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center p-4"></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute top-4 left-4 right-4 space-y-2">
          <div className="flex flex-wrap gap-2">
            <Text
              variant="span"
              size={"sm"}
              weight={"medium"}
              className="px-3 py-1 bg-blue-500/90 text-white rounded-full backdrop-blur-sm shadow-sm"
            >
              {`courseType.${CourseData[
                item.courseName as keyof typeof CourseData
              ]?.name(item.gradeLevel ?? 8)}`}
            </Text>
            <Text
              variant="span"
              size={"sm"}
              weight={"medium"}
              className="px-3 py-1 bg-purple-500/90 text-white rounded-full backdrop-blur-sm shadow-sm"
            >
              {t("hooks.form.gradeOf", {
                grade: item.gradeLevel,
              })}
            </Text>
          </div>
        </div>
      </div>
      <div className="absolute bottom-20 left-4">
        <Text variant={"h3"} className="text-white line-clamp-2 drop-shadow-lg">
          {item.prompt}
        </Text>
      </div>
      <div className="p-4 bg-gradient-to-b from-white/50 to-white dark:from-gray-800/50 dark:to-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
              <User
                size={16}
                className="text-indigo-600 dark:text-indigo-400"
              />
            </div>
            {item.authorName && (
              <Text variant="p" status="subtle">
                {item.authorName}
              </Text>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={async (e) => {
                e.stopPropagation();
                setIsLoading(true);
                await onLike();
                setIsLoading(false);
              }}
              disabled={isLoading}
              variant="solid"
              size="icon"
              rounded="full"
              bgColor="hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
              className={`group/like ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {isLoading ? (
                <Loader2
                  size={18}
                  className="text-indigo-500 dark:text-indigo-400 transition-transform duration-300 group-hover/like:scale-125 animate-spin"
                />
              ) : item.didUserLiked ? (
                <ThumbsUp
                  size={18}
                  fill="#6366f1"
                  className="text-indigo-500 dark:text-indigo-400 transition-transform duration-300 group-hover/like:scale-125"
                />
              ) : (
                <ThumbsUp
                  size={18}
                  className="text-indigo-500 dark:text-indigo-400 transition-transform duration-300 group-hover/like:scale-125"
                />
              )}
            </Button>
            <Text
              variant="span"
              size={"sm"}
              weight={"medium"}
              className="text-indigo-600 dark:text-indigo-400"
            >
              {item.likeCount || 0}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedCard;
